/**
 * Dashboard V2 — event-sourced metrics via GET /events/stats.
 *
 * Data accuracy notes:
 * - Trial→Paid rate uses isTrialConversion metadata (only present on INITIAL_PURCHASE events).
 * - DAU = unique users with feed_request events per day.
 * - Engagement rate = (likes + shares) / feed requests.
 */
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { useGetDashboardQuery, useGetEventStatsQuery } from "../../api/apiSlice";
import { TimePeriodEnum } from "../../utils/types";
import { getMockStatsData, MOCK_DASHBOARD_DATA } from "./dashboardV2MockData";

// ─── Constants ────────────────────────────────────────────────────────────────

const IS_DEV = process.env.NODE_ENV === "development";

const timePeriodOptions = [
  { label: "Last 7 Days", value: TimePeriodEnum.Last7Days },
  { label: "Last Month", value: TimePeriodEnum.LastMonth },
  { label: "Last Year", value: TimePeriodEnum.LastYear },
  { label: "Custom Date Range", value: TimePeriodEnum.Custom },
];

const CHART_COLORS = ["#4f46e5", "#06b6d4", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
const PREV_PERIOD_COLOR = "#94a3b8";
const PIE_COLORS = ["#4f46e5", "#06b6d4", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

/** Calculate % change between current and previous values */
const pctChange = (current: number, previous: number): number | null => {
  if (previous === 0) return current > 0 ? 100 : null;
  return ((current - previous) / previous) * 100;
};

const getDateRange = (period: TimePeriodEnum): { startDate: string; endDate: string } => {
  const today = dayjs();
  const endDate = today.format("YYYY-MM-DD");
  switch (period) {
    case TimePeriodEnum.Last7Days:
      return { startDate: today.subtract(7, "day").format("YYYY-MM-DD"), endDate };
    case TimePeriodEnum.LastMonth:
      return { startDate: today.subtract(30, "day").format("YYYY-MM-DD"), endDate };
    case TimePeriodEnum.LastYear:
      return { startDate: today.subtract(365, "day").format("YYYY-MM-DD"), endDate };
    default:
      return { startDate: today.subtract(7, "day").format("YYYY-MM-DD"), endDate };
  }
};

// ─── Sub-components ───────────────────────────────────────────────────────────

interface KpiCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  change?: number | null;
  info?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ title, value, subtitle, change, info }) => (
  <Card sx={{ height: "100%", border: "1px solid", borderColor: "divider" }}>
    <Box sx={{ px: 2, py: 1 }}>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        {info ? (
          <Tooltip title={info} arrow>
            <Box component="span" tabIndex={0} aria-label={`More information about ${title}`} sx={{ display: "inline-flex" }}>
              <InfoOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
            </Box>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
    <Divider />
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="h5" fontWeight={700} color="text.primary">
          {typeof value === "number" ? value.toLocaleString() : value}
        </Typography>
        {change != null && (
          <Box display="flex" alignItems="center" ml={0.5}>
            {change >= 0 ? (
              <TrendingUpIcon sx={{ fontSize: 16, color: "success.main" }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 16, color: "error.main" }} />
            )}
            <Typography variant="caption" fontWeight={600} color={change >= 0 ? "success.main" : "error.main"}>
              {Math.abs(change).toFixed(0)}%
            </Typography>
          </Box>
        )}
      </Box>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const ChartCard: React.FC<{ title: string; subtitle?: string; children: React.ReactNode; height?: number }> = ({
  title,
  subtitle,
  children,
  height = 300,
}) => (
  <Card sx={{ border: "1px solid", borderColor: "divider", height: "100%", display: "flex", flexDirection: "column" }}>
    <Box sx={{ px: 2, py: 1.5, minHeight: 52 }}>
      <Typography variant="subtitle2" color="text.primary">
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ visibility: subtitle ? "visible" : "hidden" }}>
        {subtitle || "\u00A0"}
      </Typography>
    </Box>
    <Divider />
    <CardContent sx={{ height, p: 2 }}>{children}</CardContent>
  </Card>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export const DashboardV2: React.FC = () => {
  const theme = useTheme();
  const [period, setPeriod] = useState<TimePeriodEnum>(TimePeriodEnum.Last7Days);
  const [customStartDate, setCustomStartDate] = useState<Dayjs | null>(dayjs().subtract(7, "day"));
  const [customEndDate, setCustomEndDate] = useState<Dayjs | null>(dayjs());
  const [useMock, setUseMock] = useState(() => IS_DEV && localStorage.getItem("dashv2_mock") === "true");

  const queryParams = useMemo(() => {
    if (period === TimePeriodEnum.Custom) {
      return {
        startDate: (customStartDate ?? dayjs().subtract(7, "day")).format("YYYY-MM-DD"),
        endDate: (customEndDate ?? dayjs()).format("YYYY-MM-DD"),
      };
    }
    return getDateRange(period);
  }, [period, customStartDate, customEndDate]);

  // ─── Queries ──────────────────────────────────────────────────────────────
  const statsQuery = useGetEventStatsQuery({ ...queryParams, compare: true, ...(IS_DEV && { includeTestStore: true }) }, { refetchOnMountOrArgChange: true, skip: useMock });
  const dashboardQuery = useGetDashboardQuery(queryParams, { refetchOnMountOrArgChange: true, skip: useMock });

  // ─── Mock data (dev only) ─────────────────────────────────────────────────
  const mockDays = useMemo(() => {
    const start = dayjs(queryParams.startDate);
    const end = dayjs(queryParams.endDate);
    return end.diff(start, "day") + 1;
  }, [queryParams]);
  const mockStats = useMemo(() => (useMock ? getMockStatsData(mockDays) : null), [useMock, mockDays]);

  const loading = !useMock && (statsQuery.isLoading || dashboardQuery.isLoading);
  const fetching = !useMock && (statsQuery.isFetching || dashboardQuery.isFetching);

  const refetchAll = useCallback(() => {
    if (!useMock) {
      statsQuery.refetch();
      dashboardQuery.refetch();
    }
  }, [statsQuery, dashboardQuery, useMock]);

  const handlePeriodChange = (event: SelectChangeEvent) => {
    setPeriod(event.target.value as TimePeriodEnum);
  };

  // ─── Derived data ─────────────────────────────────────────────────────────
  const stats = useMock ? mockStats : statsQuery.data;
  const daily = stats?.daily ?? [];
  const totals = stats?.totals ?? {};
  const subs = stats?.subscriptions;
  const customTrials = stats?.customTrials;

  const prevStats = stats?.previous;
  const prevDaily = prevStats?.daily ?? [];
  const prevTotals = prevStats?.totals ?? {};
  const prevSubs = prevStats?.subscriptions;
  const prevCustomTrials = prevStats?.customTrials;

  const dashData = useMock ? MOCK_DASHBOARD_DATA : dashboardQuery.data;

  // KPI values
  const totalDau = daily.length > 0 ? Math.round(daily.reduce((sum, d) => sum + d.dau, 0) / daily.length) : 0;
  const prevDau = prevDaily.length > 0 ? Math.round(prevDaily.reduce((sum, d) => sum + d.dau, 0) / prevDaily.length) : 0;
  const totalSignups = totals["user_registered"] ?? 0;
  const prevSignups = prevTotals["user_registered"] ?? 0;
  const totalOnboarding = totals["onboarding_completed"] ?? 0;
  const trialToPaidRate = subs && subs.trialStarts > 0 ? ((subs.trialConversions / subs.trialStarts) * 100).toFixed(1) : "0.0";
  const prevTrialToPaidRate = prevSubs && prevSubs.trialStarts > 0 ? (prevSubs.trialConversions / prevSubs.trialStarts) * 100 : 0;
  const totalFeeds = totals["feed_request"] ?? 0;
  const totalLikes = totals["quote_liked"] ?? 0;
  const totalShares = totals["quote_shared"] ?? 0;
  const engagementRate = totalFeeds > 0 ? (((totalLikes + totalShares) / totalFeeds) * 100).toFixed(1) : "0.0";
  const prevFeeds = prevTotals["feed_request"] ?? 0;
  const prevLikes = prevTotals["quote_liked"] ?? 0;
  const prevShares = prevTotals["quote_shared"] ?? 0;
  const prevEngagementRate = prevFeeds > 0 ? ((prevLikes + prevShares) / prevFeeds) * 100 : 0;
  const onboardingRate = totalSignups > 0 ? ((totalOnboarding / totalSignups) * 100).toFixed(1) : "0.0";
  const prevOnboarding = prevTotals["onboarding_completed"] ?? 0;
  const prevOnboardingRate = prevSignups > 0 ? (prevOnboarding / prevSignups) * 100 : 0;

  // DAU chart — merge current + previous period (aligned by day index)
  const dauChartData = daily.map((d, i) => ({
    date: dayjs(d.date).format("MMM DD"),
    DAU: d.dau,
    "Prev Period": prevDaily[i]?.dau ?? null,
  }));

  // Engagement chart
  const engagementChartData = daily.map(d => ({
    date: dayjs(d.date).format("MMM DD"),
    Likes: d.likes,
    Shares: d.shares,
  }));

  // Funnel data
  const funnelData = [
    { stage: "Signups", value: totalSignups },
    { stage: "Onboarding", value: totalOnboarding },
    { stage: "Trial Starts", value: subs?.trialStarts ?? 0 },
    { stage: "Paid", value: (subs?.paidStarts ?? 0) + (subs?.trialConversions ?? 0) },
  ];

  // Cancel reasons pie
  const cancelReasonsData = Object.entries(subs?.cancelReasons ?? {}).map(([reason, count]) => ({
    name: reason
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/^\w/, c => c.toUpperCase()),
    value: count,
  }));

  // Store split pie
  const storeSplitData = Object.entries(subs?.storeSplit ?? {}).map(([store, count]) => ({
    name: store === "APP_STORE" ? "iOS" : store === "PLAY_STORE" ? "Android" : store,
    value: count,
  }));

  // Period label
  const periodLabel = useMemo(() => {
    return `${dayjs(queryParams.startDate).format("MMM DD, YYYY")} to ${dayjs(queryParams.endDate).format("MMM DD, YYYY")}`;
  }, [queryParams]);

  const lastUpdated = useMemo(() => {
    if (statsQuery.fulfilledTimeStamp) {
      return dayjs(statsQuery.fulfilledTimeStamp).format("MMM DD, YYYY HH:mm:ss");
    }
    return "—";
  }, [statsQuery.fulfilledTimeStamp]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: "100%" }}>
        {/* Header Row */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          gap={2}
          sx={theme => ({
            bgcolor: "background.default",
            py: 1.5,
            mx: -2,
            px: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          })}
        >
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="body1" color="text.secondary">
              Time Period:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select value={period} onChange={handlePeriodChange} displayEmpty>
                {timePeriodOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {period === TimePeriodEnum.Custom && (
              <>
                <DatePicker
                  label="Start Date"
                  value={customStartDate}
                  onChange={newValue => setCustomStartDate(newValue)}
                  maxDate={customEndDate ?? dayjs()}
                  slotProps={{ textField: { size: "small", sx: { minWidth: 160 } } }}
                />
                <DatePicker
                  label="End Date"
                  value={customEndDate}
                  onChange={newValue => setCustomEndDate(newValue)}
                  minDate={customStartDate ?? undefined}
                  maxDate={dayjs()}
                  slotProps={{ textField: { size: "small", sx: { minWidth: 160 } } }}
                />
              </>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            {IS_DEV && (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={useMock}
                    onChange={(_, checked) => {
                      setUseMock(checked);
                      localStorage.setItem("dashv2_mock", String(checked));
                    }}
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    Mock
                  </Typography>
                }
                sx={{ mr: 1 }}
              />
            )}
            <Typography variant="caption" color="text.secondary">
              Last Updated: {lastUpdated} &nbsp;|&nbsp; {periodLabel}
            </Typography>
            <Tooltip title="Refresh data">
              <span style={{ display: "inline-flex" }}>
                <IconButton size="small" onClick={refetchAll} disabled={loading || fetching || useMock} aria-label="Refresh">
                  <RefreshIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Loading */}
        {loading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (
          <>
            {/* ─── KPI Row ───────────────────────────────────────────── */}
            <Grid container spacing={2} mb={3}>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KpiCard title="Avg. DAU" value={totalDau} subtitle="Daily active users" change={pctChange(totalDau, prevDau)} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KpiCard title="Signups" value={totalSignups} subtitle="New registrations" change={pctChange(totalSignups, prevSignups)} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KpiCard
                  title="Trial→Paid"
                  value={`${trialToPaidRate}%`}
                  subtitle="Conversion rate"
                  change={pctChange(parseFloat(trialToPaidRate), prevTrialToPaidRate)}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KpiCard
                  title="New Trials"
                  value={subs?.trialStarts ?? 0}
                  subtitle="Started trial"
                  change={prevSubs && prevSubs.trialStarts > 0 ? pctChange(subs?.trialStarts ?? 0, prevSubs.trialStarts) : undefined}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KpiCard
                  title="Feed Engagement Rate"
                  value={`${engagementRate}%`}
                  subtitle="(Likes + shares) / feed requests"
                  info="How much engagement did we get from feed requests?"
                  change={pctChange(parseFloat(engagementRate), prevEngagementRate)}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KpiCard
                  title="Onboarding"
                  value={`${onboardingRate}%`}
                  subtitle="Completion rate"
                  change={pctChange(parseFloat(onboardingRate), prevOnboardingRate)}
                />
              </Grid>
            </Grid>

            {/* ─── DAU Trend Chart ───────────────────────────────────── */}
            <Box mb={2}>
              <ChartCard title="Daily Active Users" subtitle="Unique users who requested a feed per day">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dauChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="dauGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={CHART_COLORS[0]} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={CHART_COLORS[0]} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <RechartsTooltip />
                    <Legend />
                    <Area type="monotone" dataKey="DAU" stroke={CHART_COLORS[0]} fillOpacity={1} fill="url(#dauGradient)" strokeWidth={2} />
                    <Area
                      type="monotone"
                      dataKey="Prev Period"
                      stroke={PREV_PERIOD_COLOR}
                      fillOpacity={0}
                      strokeWidth={1.5}
                      strokeDasharray="5 3"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
            </Box>

            {/* ─── Engagement Chart ──────────────────────────────────── */}
            <Box mb={2}>
              <ChartCard title="Engagement Activity" subtitle="Likes and shares per day (stacked)">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={engagementChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} interval={Math.max(0, Math.floor(engagementChartData.length / 8) - 1)} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="Likes" stackId="engagement" fill={CHART_COLORS[2]} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Shares" stackId="engagement" fill={CHART_COLORS[3]} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Box>

            {/* ─── Funnel + Pie Charts Row ───────────────────────────── */}
            <Grid container spacing={2} mb={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <ChartCard title="Conversion Funnel" subtitle="Signup → Onboarding → Trial → Paid" height={280}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={funnelData} layout="vertical" margin={{ top: 10, right: 30, left: 80, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="stage" tick={{ fontSize: 12 }} width={80} />
                      <RechartsTooltip />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {funnelData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <ChartCard title="Cancellation Reasons" height={280}>
                  {cancelReasonsData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={cancelReasonsData} cx="50%" cy="45%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                          {cancelReasonsData.map((_, index) => (
                            <Cell key={`cancel-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip formatter={(value, name) => [`${value}`, name]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                      <Typography variant="body2" color="text.secondary">
                        No cancellations
                      </Typography>
                    </Box>
                  )}
                </ChartCard>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <ChartCard title="Store Split" subtitle="iOS vs Android" height={280}>
                  {storeSplitData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={storeSplitData} cx="50%" cy="45%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                          {storeSplitData.map((_, index) => (
                            <Cell key={`store-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip formatter={(value, name) => [`${value}`, name]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                      <Typography variant="body2" color="text.secondary">
                        No subscription data
                      </Typography>
                    </Box>
                  )}
                </ChartCard>
              </Grid>
            </Grid>

            {/* ─── Subscription Activity Cards ───────────────────────── */}
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.primary" mb={1}>
                Subscription Activity
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard title="Starts" value={subs?.subscriptionStarts ?? 0} subtitle="All initial purchases" />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard title="Trial Starts" value={subs?.trialStarts ?? 0} subtitle="Free trial begins" />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard title="Trial Conversions" value={subs?.trialConversions ?? 0} subtitle="Trial → Paid" />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard title="Renewals" value={subs?.renewals ?? 0} subtitle="Auto-renewals" />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard title="Cancellations" value={subs?.cancellations ?? 0} subtitle="Cancelled subs" />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard title="Expirations" value={subs?.expirations ?? 0} subtitle="Lost access" />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard title="Paid Starts" value={subs?.paidStarts ?? 0} subtitle="Direct paid (no trial)" />
                </Grid>
              </Grid>
            </Box>

            {/* ─── Custom Trial Activity ─────────────────────────────── */}
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.primary" mb={1}>
                Custom Trial Activity
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard
                    title="Trials Started"
                    value={customTrials?.started ?? 0}
                    subtitle="Custom trials in period"
                    change={prevCustomTrials ? pctChange(customTrials?.started ?? 0, prevCustomTrials.started) : undefined}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard
                    title="Trial Conversions"
                    value={customTrials?.converted ?? 0}
                    subtitle="Trial → Paid"
                    change={prevCustomTrials ? pctChange(customTrials?.converted ?? 0, prevCustomTrials.converted) : undefined}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard
                    title="Conversion Rate"
                    value={`${customTrials?.conversionRate?.toFixed(1) ?? "0.0"}%`}
                    subtitle="Trial to paid rate"
                    change={prevCustomTrials ? pctChange(customTrials?.conversionRate ?? 0, prevCustomTrials.conversionRate) : undefined}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard
                    title="Avg Days to Convert"
                    value={customTrials?.avgDaysToConvert != null ? customTrials.avgDaysToConvert.toFixed(1) : "—"}
                    subtitle="Average trial-to-paid duration"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* ─── Current Snapshot ──────────────────────────────────── */}
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.primary" mb={1}>
                Current Snapshot{" "}
                <Typography variant="caption" color="text.secondary" component="span">
                  — independent of date range
                </Typography>
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard title="Active Subscribers" value={dashData?.totalActiveSubscribers ?? 0} subtitle="Currently paying" />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard title="Trialing" value={dashData?.totalTrialing ?? 0} subtitle="On free trial" />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard title="Total With Access" value={dashData?.totalWithAccess ?? 0} subtitle="Active + trial + grace" />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard title="Churned" value={dashData?.totalChurned ?? 0} subtitle="Expired/paused/billing retry" />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard title="Active Custom Trials" value={dashData?.totalActiveCustomTrials ?? 0} subtitle="Currently on custom trial" />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <KpiCard title="Expired Custom Trials" value={dashData?.totalExpiredCustomTrials ?? 0} subtitle="Trial expired, no sub" />
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};
